import React, { createContext, useContext, useEffect, useState } from 'react';

interface ThemeColors {
    primary: {
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
    };
    secondary: {
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
    };
    third: {
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
    };
    green?: {
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
    };
    blue?: {
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
    };
}

interface ThemeVariant {
    light: ThemeColors;
    dark: ThemeColors;
}

interface ThemeContextType {
    currentTheme: string;
    isDarkMode: boolean;
    isSystemTheme: boolean;
    colors: ThemeColors;
    toggleThemeMode: (mode: 'light' | 'dark' | 'system') => void;
}

const CHAPLIN_THEME: ThemeVariant = {
    light: {
        primary: {
            100: '#FFFFFF',
            200: '#F5F5F5',
            300: '#E0E0E0',
            400: '#CCCCCC',
            500: '#B3B3B3'
        },
        secondary: {
            100: '#A6A6A6',
            200: '#8C8C8C',
            300: '#737373',
            400: '#595959',
            500: '#404040'
        },
        third: {
            100: '#333333',
            200: '#262626',
            300: '#1A1A1A',
            400: '#0D0D0D',
            500: '#000000'
        },
        green: {
            100: '#2ABB7F',
            200: '#22A06B',
            300: '#216E4E',
            400: '#164B35',
            500: '#1C3329'
        },
        blue: {
            100: '#388BFF',
            200: '#1D7AFC',
            300: '#0C66E4',
            400: '#0055CC',
            500: '#09326C'
        }
    },
    dark: {
        primary: {
            100: '#DEE4EA',
            200: '#C7D1DB',
            300: '#B6C2CF',
            400: '#9FADBC',
            500: '#8C9BAB'
        },
        secondary: {
            100: '#404040',
            200: '#595959',
            300: '#737373',
            400: '#8C8C8C',
            500: '#A6A6A6'
        },
        third: {
            100: '#282E33',
            200: '#22272B',
            300: '#1D2125',
            400: '#161A1D',
            500: '#101214'
        },
        green: {
            100: '#2ABB7F',
            200: '#22A06B',
            300: '#216E4E',
            400: '#164B35',
            500: '#1C3329'
        },
        blue: {
            100: '#388BFF',
            200: '#1D7AFC',
            300: '#0C66E4',
            400: '#0055CC',
            500: '#09326C'
        }
    }
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isSystemTheme, setIsSystemTheme] = useState(true);

    // Effet initial pour charger les préférences de mode
    useEffect(() => {
        const savedMode = localStorage.getItem('themeMode');
        if (savedMode === 'light' || savedMode === 'dark') {
            setIsSystemTheme(false);
            setIsDarkMode(savedMode === 'dark');
        } else {
            // Utiliser les préférences système
            setIsSystemTheme(true);
            setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
        }
    }, []);

    // Effet pour gérer les changements de préférence système
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = (e: MediaQueryListEvent) => {
            if (isSystemTheme) {
                setIsDarkMode(e.matches);
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [isSystemTheme]);

    // Effet pour appliquer les changements - thème fixé sur Chaplin
    useEffect(() => {
        const root = document.documentElement;
        const currentColors = CHAPLIN_THEME[isDarkMode ? 'dark' : 'light'];

        // Appliquer les variables CSS pour chaque type de couleur
        Object.entries(currentColors).forEach(([type, shades]) => {
            Object.entries(shades as Record<string, string>).forEach(([shade, color]) => {
                root.style.setProperty(`--${type}-${shade}`, color);
            });
        });

        // Gérer le mode sombre
        if (isDarkMode) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }

        // Sauvegarder les préférences de mode uniquement
        localStorage.setItem('themeMode', isSystemTheme ? 'system' : isDarkMode ? 'dark' : 'light');
    }, [isDarkMode, isSystemTheme]);

    const toggleThemeMode = (mode: 'light' | 'dark' | 'system') => {
        if (mode === 'system') {
            setIsSystemTheme(true);
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setIsDarkMode(prefersDark);
        } else {
            setIsSystemTheme(false);
            setIsDarkMode(mode === 'dark');
        }
    };

    return (
        <ThemeContext.Provider
            value={{
                currentTheme: 'chaplin',
                isDarkMode,
                isSystemTheme,
                colors: CHAPLIN_THEME[isDarkMode ? 'dark' : 'light'],
                toggleThemeMode
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
}